import { Injectable } from '@angular/core';
import {
  RolUsers,
  ConfigFinishDefault,
  TareaTipo,
  TareaEstado,
  TareaTipoFinalizacion,
  Tarea,
  Sede,
} from '@app/shared';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { EndpointService } from '@app/core';
import { FormService, ConfigFields } from './form-service';
import { result } from 'lodash';
import { Logger } from '@app/core/utilities/logger.service';

export type actionManageTask = 'create' | 'edit';
const log = new Logger('TaskService');

@Injectable()
export class TaskService {
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) { }

  /**
   * Finaliza una tarea que funciones con el endpoint 'instanceMethods'.
   *
   * @param {ConfigFinishDefault} config
   * @returns {Promise<boolean>}
   * @memberof TaskService
   */
  finishDefault(config: ConfigFinishDefault): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .post(this.api.get('instanceMethods'), config, {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 200) {
              resolve(true);
              this.toastr.success(`La tarea se finalizó correctamente.`, 'Operación exitosa');
            } else {
              resolve(false);
            }
          },
          err => {
            resolve(false);
            reject(err);
          }
        );
    });
  }

  /**
   * Retorna los roles del sistema.
   *
   * @returns {Promise<RolUsers>}
   * @memberof TaskService
   */
  getRoles(): Promise<RolUsers[]> {
    return new Promise((resolve, reject) => {
      this.http.get<RolUsers[]>(this.api.get('rolesUsers')).subscribe(
        data => {
          resolve(data);
        },
        err => reject(err)
      );
    });
  }

  /**
   * Consulta los posibles tipos de una tarea.
   *
   * @returns {Promise<TareaTipo[]>}
   * @memberof TaskService
   */
  getTypes(): Promise<TareaTipo[]> {
    return new Promise((resolve, reject) => {
      this.http.get<TareaTipo[]>(this.api.get('defaultTypeTask')).subscribe(
        data => {
          resolve(data);
        },
        err => reject(err)
      );
    });
  }

  /**
   * Consulta los posibles estados de una tarea.
   *
   * @returns {Promise<TareaEstado[]>}
   * @memberof TaskService
   */
  getStatus(): Promise<TareaEstado[]> {
    return new Promise((resolve, reject) => {
      this.http.get<TareaEstado[]>(this.api.get('defaultStatusTask')).subscribe(
        data => {
          resolve(data);
        },
        err => reject(err)
      );
    });
  }

  /**
   * Consulta los posibles tipos de finalizaciones de una tarea.
   *
   * @returns {Promise<TareaTipoFinalizacion[]>}
   * @memberof TaskService
   */
  getCompletionTypes(): Promise<TareaTipoFinalizacion[]> {
    return new Promise((resolve, reject) => {
      this.http.get<TareaTipoFinalizacion[]>(this.api.get('defaultTypeCompletion')).subscribe(
        data => {
          resolve(data);
        },
        err => reject(err)
      );
    });
  }

  /**
   * Retorna los controles necesarios para crear un formulario dinámico.
   *
   * @param {Partial<ConfigFields>} config
   * @returns
   * @memberof TaskService
   */
  getControls(config: Partial<ConfigFields>) {
    const fs = new FormService();
    return fs.getFields(config);
  }

  /**
   * Consulta una tarea predeterminada en especifico con el id de la tarea
   *
   * @param {number} idTask
   * @returns {Promise<Tarea>}
   * @memberof TaskService
   */
  getTask(idTask: number): Promise<Tarea> {
    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler()
        .get<Tarea>(this.api.get('defaultTask', [idTask]))
        .subscribe(
          task => {
            resolve(task);
          },
          err => {
            reject(null);
            log.debug(err);
          }
        );
    });
  }

  /**
   * Asociacion de un tramite especifico a el actual
   *
   * @param {number} idTramiteOrigen
   * @param {number} idTramiteDetino
   * @memberof TaskService
   */
  associationProcess(idTramiteOrigen: number, idTramiteDestino: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dataForm = {
        id_proceso_origen: idTramiteOrigen,
        id_proceso_actual: idTramiteDestino
      };
      this.http.post(this.api.get('associateProcess'), dataForm)
        .subscribe(
          _resp => {
            resolve(true);
          },
          err => {
            reject(false);
            log.debug(err);
          }
        );
    });
  }

  /**
   * Consulta una tarea de la instancia en especifico
   *
   * @returns {Promise<Tarea[]>}
   * @memberof TaskService
   */
  getInstanceTask(idTask: number): Promise<Tarea> {
    return new Promise((resolve, reject) => {
      this.http.get<Tarea>(this.api.get('associatedTask', [idTask])).subscribe(
        data => {
          resolve(data);
        },
        err => reject(err)
      );
    });
  }

  /**
   * Consulta las sedes de una empresa
   *
   * @param {number} idCompany
   * @returns {Promise<Sede[]>}
   * @memberof TaskService
   */
  getSedes(idCompany: number): Promise<Sede[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Sede[]>(this.api.get('sedesManagement', [idCompany])).subscribe(
        sedes => {
          resolve(sedes);
        },
        err => {
          reject(err);
        }
      );
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/flow/task/shared/task.service.ts